/**
 * backgroundJobs.ts
 * Persists active upload jobs to localStorage so they survive modal close.
 * AdminDashboard polls these and shows a status bar.
 */

export type JobType = "form106" | "worklog";
export type JobStatus = "processing" | "done" | "failed";

export interface BackgroundJob {
  jobId: string;
  type: JobType;
  label: string; // e.g. "Form 106 — 2025" or "Work Log — 05/2026"
  status: JobStatus;
  startedAt: number;
  progress?: { processed: number; total: number };
  result?: any;
  error?: string;
}

const STORAGE_KEY = "mata_background_jobs";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // discard jobs older than 24h

function load(): BackgroundJob[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const jobs: BackgroundJob[] = JSON.parse(raw);
    // Prune old completed jobs
    return jobs.filter(
      (j) => j.status === "processing" || Date.now() - j.startedAt < MAX_AGE_MS,
    );
  } catch {
    return [];
  }
}

function save(jobs: BackgroundJob[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {}
}

export const BackgroundJobs = {
  add(job: Omit<BackgroundJob, "startedAt" | "status">): void {
    const jobs = load();
    jobs.push({ ...job, status: "processing", startedAt: Date.now() });
    save(jobs);
  },

  update(jobId: string, patch: Partial<BackgroundJob>): void {
    const jobs = load();
    const idx = jobs.findIndex((j) => j.jobId === jobId);
    if (idx !== -1) {
      jobs[idx] = { ...jobs[idx], ...patch };
      save(jobs);
    }
  },

  remove(jobId: string): void {
    save(load().filter((j) => j.jobId !== jobId));
  },

  getAll(): BackgroundJob[] {
    return load();
  },

  getActive(): BackgroundJob[] {
    return load().filter((j) => j.status === "processing");
  },
};
